import type { PortalDeploymentItem } from '@/components/client-portal/DeploymentBadge'
import { trackFeatureUsed } from '@/lib/analytics'
import { createLogger } from '@/lib/logger'

import type {
  AcceptClientPortalInviteDTO,
  BulkUpdateNotificationPreferencesDto,
  ClientPortalPermissionTemplate,
  ClientPortalProjectTaskSettings,
  CreateClientPortalInviteDTO,
  CreateClientPortalPermissionTemplateDTO,
  CreateClientPortalTaskDTO,
  CreateNotePage,
  CreateThreadComment,
  DocumentResponse,
  ExpenseCodingConfigDTO,
  ExpenseCodingImportCommitResultDTO,
  ExpenseCodingImportPreviewDTO,
  InvoiceApprovalRequestWithTrail,
  NotePageSummaryWithCreator,
  NotePageWithCreator,
  NotificationPreference,
  PaginatedResponse,
  PortalCodeItemDTO,
  PortalCreateCodingItemDTO,
  PortalDeployment,
  PortalExpenseCodingItemDTO,
  PortalExpenseCodingListDTO,
  PortalInvoiceApproval,
  PortalPermissionMap,
  PortalSharedDocumentsResponse,
  ProjectTaskCapabilityMap,
  TaskPriorityEnum,
  Thread,
  ThreadComment,
  ThreadCommentChangesResponse,
  ThreadCommentReaction,
  ThreadReaction,
  TicketDraft,
  TicketDraftListItem,
  UpdateClientPortalPermissionTemplateDTO,
  UpdateClientPortalProjectTaskSettingsDTO,
  UpdateClientPortalSettingsDTO,
  UpdateClientPortalSharesDTO,
  UpdateClientPortalTaskStatusDTO,
  UpdateNoteInput,
  UpdateNotePage,
  UpdateNotificationPreferenceDto,
  UpsertTicketDraft,
} from '@scrambl/shared'

import { apiClient, emptyResponseToNull } from './api-client'
import { requestBlob } from './base-api'

const logger = createLogger('lib/api/client-portal.api')

export interface ClientPortalInviteData {
  organizationId: string
  organizationName?: string
  organizationLogo?: string
  entityId: string
  entityName?: string
  email?: string
  name?: string
  status?: string
  expiresAt?: string
}

export interface ClientPortalOverview {
  settings?: {
    isEnabled: boolean
    allowNoteEdits: boolean
    allowNoteCreate: boolean
    allowNoteDelete: boolean
    allowThreadComments: boolean
    allowThreadReactions: boolean
    allowNoteFileUploads: boolean
    allowThreadFileUploads: boolean
    allowPortalDocumentUploads: boolean
    allowPortalLinkUploads: boolean
    allowPortalDocumentDeletes: boolean
    allowPortalLinkDeletes: boolean
    allowTicketReplies: boolean
    allowTicketClose: boolean
    allowTicketCreate: boolean
    showNotes: boolean
    showDocuments: boolean
    showLinkedDocuments: boolean
    showDiscussions: boolean
    showSupport: boolean
    showKnowledge: boolean
    showRetainers: boolean
    showRetainerHistory: boolean
    showRetainerProjectBreakdown: boolean
    showRetainerMemberBreakdown: boolean
    showRetainerRoleBreakdown: boolean
    showRetainerOverageProjection: boolean
    showInvoices?: boolean
    showPaymentHistory?: boolean
    allowInvoiceApprove?: boolean
    showExpenseCoding?: boolean
    allowExpenseCoding?: boolean
    showAssistant?: boolean
    appliedTemplateId?: string | null
  } | null
  shares?: {
    projectIds: string[]
    noteIds: string[]
    threadIds: string[]
    documentIds: string[]
    documentCategoryIds: string[]
    kbArticleIds: string[]
    kbCategoryIds: string[]
  }
  access?: Array<{
    id: string
    status: string
    createdAt?: string
    lastAccessedAt?: string | null
    permissions?: PortalPermissionMap | null
    user?: {
      id: string
      firstName?: string | null
      lastName?: string | null
      displayName?: string | null
      email?: string | null
    }
    inviter?: {
      id: string
      firstName?: string | null
      lastName?: string | null
      displayName?: string | null
      email?: string | null
    }
  }>
  invites?: Array<{
    id: string
    email: string
    name?: string | null
    status: string
    createdAt?: string
    expiresAt?: string
    inviter?: {
      id: string
      firstName?: string | null
      lastName?: string | null
      displayName?: string | null
      email?: string | null
    }
    user?: {
      id: string
      firstName?: string | null
      lastName?: string | null
      displayName?: string | null
      email?: string | null
    }
  }>
  organizationId?: string
}

export interface ClientPortalShareableResources {
  projects: Array<{
    id: string
    name: string
    status?: string
    projectType?: string
    /** True when the project has the buckets feature enabled. */
    bucketsEnabled?: boolean
  }>
  notes: Array<{
    id: string
    title: string
    moduleType?: string | null
    moduleId?: string | null
    updatedAt?: string
  }>
  threads: Array<{
    id: string
    title: string
    moduleType?: string | null
    moduleId?: string | null
    updatedAt?: string
  }>
  documents: Array<{
    id: string
    displayName: string
    type?: string | null
    fileName?: string | null
    url?: string | null
    categoryId?: string | null
    updatedAt?: string
  }>
  documentCategories: Array<{
    id: string
    name: string
    icon?: string | null
    color?: string | null
    parentId?: string | null
  }>
  kbArticles: Array<{
    id: string
    title: string
    categoryId?: string | null
    updatedAt?: string
  }>
  kbCategories: Array<{
    id: string
    name: string
  }>
}

// ---------------------------------------------------------------------------
// Portal Tickets (Phase 2 — customer-facing support conversations)
// ---------------------------------------------------------------------------

export interface PortalTicketStageInfo {
  id: string
  name: string
  color: string
  stageTypeId: string
}

export interface PortalReferenceStatus {
  id: string
  name: string
  color: string | null
  statusTypeId: string | null
}

export interface PortalTaskReference {
  id: string
  taskNumber: string | null
  name: string
  projectId: string
  projectName: string
  status: PortalReferenceStatus
  priority: string | null
  dueDate: string | null
  updatedAt: string
}

export interface PortalTicketReference {
  id: string
  ticketNumber: string
  subject: string
  status: PortalReferenceStatus
  priority: string | null
  updatedAt: string
}

export interface PortalTicketSummary {
  id: string
  ticketNumber: string
  subject: string
  stage: string
  stageInfo: PortalTicketStageInfo | null
  priority: string
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  closedAt: string | null
  requesterName: string | null
  requesterEmail: string | null
  typeDefId?: string | null
  typeDef?: ClientPortalTicketType | null
  // Slim deployment refs, present only when the portal's showDeployments
  // setting is on (backend strips it otherwise). Resolved server-side for
  // magic-link ticket views too.
  deploymentItems?: PortalDeploymentItem[]
}

export interface PortalTicketDetail extends PortalTicketSummary {
  description: string | null
  taskReferences?: PortalTaskReference[]
}

// CSAT satisfaction survey state for a resolved portal ticket.
export interface PortalTicketCsat {
  rating: number | null
  comment: string | null
  respondedAt: string | null
  editable: boolean
}

export interface PortalTicketCommunicationAttachment {
  id: string
  fileName: string
  fileSize: number
  mimeType: string
}

// A file staged for a ticket message (create or reply): uploaded first, then
// referenced by storage key when submitting. Mirrors the backend DTO
// (TicketAttachmentInput) but declared explicitly so all fields are required.
export interface TicketStagedAttachment {
  storageKey: string
  fileName: string
  fileSize: number
  mimeType: string
  previewUrl?: string
}

// The signed-in portal user's unsent reply composer for a ticket
// (server-synced so it follows the user across devices).
export interface PortalTicketReplyDraft {
  ticketId: string
  bodyHtml: string
  ccText: string
  replyDraftId: string
  attachments: TicketStagedAttachment[]
  updatedAt: string
}

export interface PortalTicketCommunication {
  id: string
  direction: 'INBOUND' | 'OUTBOUND'
  status: string
  subject: string | null
  body: string | null
  bodyHtml: string | null
  fromEmail: string | null
  fromName: string | null
  toEmails: string[]
  ccEmails: string[]
  sentAt: string | null
  createdAt: string
  attachments: PortalTicketCommunicationAttachment[]
}

export interface ClientPortalProject {
  id: string
  name: string
  status: string
  statusColor?: string | null
  statusLabel?: string | null
  startDate?: string
  endDate?: string
  logoUrl?: string | null
  entityId?: string | null
  progress?: {
    completedTaskCount: number
    totalTaskCount: number
    percent: number
  }
  currentSprint?: {
    id: string
    name: string
    endsAt: string | null
    tasksDueThisWeek: number
  }
}

export type AttentionItemType =
  | 'mention'
  | 'thread_unread'
  | 'note_unread'
  | 'document_shared'
  | 'retainer_threshold'
  | 'task_ready'
  | 'ticket_reply'

export interface AttentionItem {
  id: string
  type: AttentionItemType
  title: string
  subtitle?: string
  href: string
  occurredAt: string
  severity: 'high' | 'medium' | 'low'
  notificationId?: string
}

export interface AttentionResponse {
  items: AttentionItem[]
  totalCount: number
}

export interface PortalNotificationFeedItem {
  id: string
  type: string
  title: string
  message?: string
  href: string
  occurredAt: string
  readAt?: string
  priority: string
  actor?: { id: string; name: string; avatarUrl?: string }
}

export interface PortalNotificationFeedResponse {
  items: PortalNotificationFeedItem[]
  unreadCount: number
  totalCount: number
  hasMore: boolean
}

// A task in a status the org designated as "waiting on the client"
// (share taskSettings.clientActionStatusIds). Powers the home
// "Waiting on you" widget.
export interface ClientActionTask {
  id: string
  title: string
  projectId: string
  projectName: string
  statusId: string
  statusName: string
  statusColor: string | null
  priority: string | null
  dueDate: string | null
  updatedAt: string
}

export interface ClientActionTasksResponse {
  items: ClientActionTask[]
  totalCount: number
}

export type ActivityItemType =
  | 'note_shared'
  | 'thread_message_posted'
  | 'sprint_started'
  | 'sprint_completed'
  | 'task_completed_batch'
  | 'project_status_changed'

export interface ActivityItem {
  id: string
  type: ActivityItemType
  title: string
  href?: string
  actor?: { id: string; name: string; avatarUrl?: string }
  occurredAt: string
}

export interface ActivityResponse {
  items: ActivityItem[]
  hasMore: boolean
}

export interface MyEntity {
  entityId: string
  entityName: string
  organization: { id: string; name: string; logoUrl?: string }
  unreadAttentionCount: number
}

export interface MyEntitiesResponse {
  entities: MyEntity[]
}

export interface ClientPortalProjectDetail {
  project: {
    id: string
    name: string
    description?: string | null
    status: string
    projectType?: string
    startDate?: string
    endDate?: string
    logoUrl?: string | null
    settings?: Record<string, unknown>
  }
  share: {
    id: string
    taskSettings: ClientPortalProjectTaskSettings
  }
}

export interface ClientPortalCustomFieldValue {
  id: string
  fieldId: string
  value: unknown
  customField: {
    id: string
    fieldName: string
    fieldType: string
    label?: string | null
  }
}

export interface ClientPortalTask {
  id: string
  name: string
  description?: string | null
  status: string
  statusTypeId?: string | null
  priority?: string | null
  dueDate?: string | null
  groupId?: string | null
  groupName?: string | null
  sprintId?: string | null
  sprintOrder?: number | null
  bucketId?: string | null
  bucketOrder?: number | null
  cycleInstanceId?: string | null
  parentTaskId?: string | null
  isRecurring?: boolean
  recurringPattern?: unknown
  parentRecurringTaskId?: string | null
  recurringInstanceNum?: number | null
  isRecurringTemplate?: boolean
  order?: string | null
  commentCount?: number
  subtaskCount?: number
  availableStatuses?: ClientPortalStatus[]
  createdAt?: string
  updatedAt?: string
  completedAt?: string | null
  customFieldValues?: ClientPortalCustomFieldValue[]
  taskTypeDefId?: string | null
  taskTypeDef?: ClientPortalTaskTypeDef | null
  // Slim deployment refs, present only when the portal's showDeployments
  // setting is on (backend strips it otherwise).
  deploymentItems?: PortalDeploymentItem[]
}

/** Lightweight task-type summary for rendering the type chip in the portal. */
export interface ClientPortalTaskTypeDef {
  id: string
  key: string
  name: string
  icon?: string | null
  color?: string | null
  displayStyle?: string | null
}

export interface ClientPortalTaskCommentRequest {
  threadId?: string
  parentCommentId?: string | null
  content: string
  clientMessageId?: string | null
}

export interface ClientPortalTaskCommentsResponse {
  thread: Thread
  comments: ThreadComment[]
  totalComments: number
  allowTaskComments: boolean
  allowTaskCommentCreate: boolean
  allowTaskCommentEdit: boolean
  allowTaskCommentDelete: boolean
  allowThreadReactions: boolean
}

export interface ClientPortalSubtask {
  id: string
  name: string
  description?: string | null
  status: string
  statusTypeId?: string | null
  priority?: string | null
  dueDate?: string | null
  groupId?: string | null
  groupName?: string | null
  sprintId?: string | null
  sprintOrder?: number | null
  bucketId?: string | null
  bucketOrder?: number | null
  cycleInstanceId?: string | null
  parentTaskId?: string | null
  isRecurring?: boolean
  recurringPattern?: unknown
  parentRecurringTaskId?: string | null
  recurringInstanceNum?: number | null
  isRecurringTemplate?: boolean
  completedAt?: string | null
  order?: string | null
  createdAt?: string
  updatedAt?: string
  commentCount?: number
  subtaskCount?: number
  availableStatuses?: ClientPortalStatus[]
  customFieldValues?: ClientPortalCustomFieldValue[]
  taskTypeDefId?: string | null
  taskTypeDef?: ClientPortalTaskTypeDef | null
  deploymentItems?: PortalDeploymentItem[]
}

export interface ClientPortalSubtasksResponse {
  subtasks: ClientPortalSubtask[]
  taskSettings?: ClientPortalProjectTaskSettings
}

export interface ClientPortalTaskGroup {
  id: string
  name: string
  color?: string | null
  order?: number
  canAddTasks?: boolean
}

export interface ClientPortalSprint {
  id: string
  name: string
  goal?: string | null
  status: string
  startDate: string
  endDate: string
  order?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface ClientPortalBucket {
  id: string
  name: string
  description?: string | null
  color?: string | null
  startDate?: string | null
  endDate?: string | null
  order?: string | null
  isArchived?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface ClientPortalCycle {
  id: string
  name: string
  description?: string | null
  status: string
  periodStart: string
  periodEnd: string
  order?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface ClientPortalStatus {
  id: string
  name: string
  statusTypeId: string
  color?: string
  order?: number
  isDefault?: boolean
}

export interface ClientPortalCustomField {
  id: string
  fieldName: string
  fieldType: string
  label?: string | null
  settings?: Record<string, unknown>
  order?: number
  isStandard?: boolean
  collectionId?: string | null
}

export interface ClientPortalShareWithSettings {
  id: string
  moduleType: string
  moduleId: string
  // Resolved project name for PROJECT shares (null for other module types).
  moduleName?: string | null
  // PROJECT shares only: the project was deleted or moved to another entity,
  // so the share no longer resolves. Surfaced in Setup for explicit removal.
  stale?: boolean
  hidden: boolean
  taskSettings?: ClientPortalProjectTaskSettings | null
  settingsUpdatedAt?: string | null
  createdAt?: string
  lastViewedAt?: string | null
}

// A portal member's sparse per-project capability override. `capabilities`
// holds only the keys an admin customized; absent keys inherit the project
// default.
export interface ClientPortalShareMemberSetting {
  accessId: string
  capabilities: ProjectTaskCapabilityMap
  settingsUpdatedAt?: string | null
}

// A single member's capability override for one project share (keyed by shareId).
export interface ClientPortalMemberProjectSetting {
  shareId: string
  capabilities: ProjectTaskCapabilityMap
  settingsUpdatedAt?: string | null
}

export interface ClientPortalNote {
  id: string
  organizationId?: string
  creatorId?: string
  categoryId?: string | null
  title: string
  content?: unknown
  moduleType?: string | null
  moduleId?: string | null
  contentHtml?: string | null
  visibility?: string
  isPinned?: boolean
  color?: string | null
  isCollection?: boolean
  pageCount?: number
  deletedAt?: string | null
  createdAt?: string
  updatedAt?: string
  createdBy?: {
    id: string
    firstName?: string | null
    lastName?: string | null
    displayName?: string | null
    image?: string | null
  }
}

export interface ClientPortalThread {
  id: string
  title: string
  description?: string | null
  threadType?: string | null
  moduleType?: string | null
  moduleId?: string | null
  createdAt?: string
  updatedAt?: string
  createdBy?: {
    id: string
    firstName?: string | null
    lastName?: string | null
    displayName?: string | null
    image?: string | null
  }
}

export interface ClientPortalThreadDetailResponse {
  thread: ClientPortalThread
  comments: ThreadComment[]
  totalComments: number
  allowThreadComments: boolean
  allowThreadReactions: boolean
}

export interface ClientPortalNoteActionItem {
  id: string
  noteId: string
  notePageId: string | null
  organizationId: string
  text: string
  description: string | null
  order: number
  assigneeId: string | null
  assigneeIds: string[]
  dueDate: string | null
  groupId: string | null
  taskTypeDefId: string | null
  targetProjectId: string | null
  isConverted: boolean
  convertedTaskId: string | null
  createdAt: string
  updatedAt: string
  assignee?: {
    id: string
    firstName: string | null
    lastName: string | null
    displayName: string | null
    image: string | null
  } | null
}

export interface ClientPortalConvertActionItemsOptions {
  groupId?: string
  status?: string
  sprintId?: string
  cycleInstanceId?: string
  priority?: TaskPriorityEnum
  dueDate?: string
  assigneeIds?: string[]
  tagIds?: string[]
  customFields?: Record<string, unknown>
}

export interface ClientPortalSummary {
  entity: {
    id: string
    name: string
    logoUrl?: string | null
  }
  organization: {
    id: string
    name: string
    logo: string | null
    slug: string
  }
  settings: {
    isEnabled: boolean
    allowNoteEdits: boolean
    allowNoteCreate: boolean
    allowNoteDelete: boolean
    allowThreadComments: boolean
    allowThreadReactions: boolean
    allowNoteFileUploads: boolean
    allowThreadFileUploads: boolean
    allowPortalDocumentUploads: boolean
    allowPortalLinkUploads: boolean
    allowPortalDocumentDeletes: boolean
    allowPortalLinkDeletes: boolean
    allowTicketReplies: boolean
    allowTicketClose: boolean
    allowTicketCreate: boolean
    showNotes: boolean
    showDocuments: boolean
    showLinkedDocuments: boolean
    showDiscussions: boolean
    showSupport: boolean
    showKnowledge: boolean
    showRetainers: boolean
    showRetainerHistory?: boolean
    showRetainerProjectBreakdown?: boolean
    showRetainerMemberBreakdown?: boolean
    showRetainerRoleBreakdown?: boolean
    showRetainerOverageProjection?: boolean
    showDeployments: boolean
    showInvoices?: boolean
    showPaymentHistory?: boolean
    allowInvoiceApprove?: boolean
    showExpenseCoding?: boolean
    allowExpenseCoding?: boolean
    showAssistant?: boolean
  }
  counts: {
    projects: number
    notes: number
    threads: number
    openTickets?: number
  }
  ticketTypes?: ClientPortalTicketType[]
  branding?: {
    logoUrl: string | null
    accentColor: string | null
    sidebarColor: string | null
    secondaryColor: string | null
    coverImageUrl: string | null
    coverImagePosition: string | null
  }
  /**
   * True when the viewer is an internal org member previewing the portal,
   * not a real client. Internal viewers are read-only.
   */
  isInternalViewer?: boolean
}

export interface ClientPortalTicketType {
  id: string
  key: string
  name: string
  description?: string | null
  icon?: string | null
  color?: string | null
  order?: number
}

export interface ClientPortalRetainerPeriod {
  id: string
  periodStart: string
  periodEnd: string
  includedHours: number
  rolloverInHours: number
  adjustmentHours: number
  usedHours: number
  remainingHours: number
  overageHours: number
  pendingHours?: number
  unallocatedHours?: number
  forecastedUsageHours: number | null
  projectedOverageHours: number | null
  projectedOverageDate: string | null
  usagePercent: number
  status: string
  projectBreakdown: Array<{ id?: string | null; name: string; hours: number }>
  memberBreakdown: Array<{ id?: string | null; name: string; hours: number }>
  roleBreakdown: Array<{ id?: string | null; name: string; hours: number }>
  adjustments: Array<{
    id: string
    adjustmentType: string
    hours: number
    reason: string
    createdAt: string
  }>
}

export interface ClientPortalRetainer {
  contract: {
    id: string
    title: string
    planName?: string | null
    responseSla?: string | null
    projectName?: string | null
  }
  currentPeriod: ClientPortalRetainerPeriod
  previousPeriods: ClientPortalRetainerPeriod[]
}

export interface ClientPortalInvoicePayment {
  id: string
  amount: number
  paymentDate: string
  paymentMethod: string
}

export interface ClientPortalInvoice {
  id: string
  invoiceNumber: string
  title: string
  status: string
  issueDate: string
  dueDate: string
  currency: string
  totalAmount: number
  amountPaid: number
  balance: number
  /** Linked authorization referenceNumber; the DTO whitelist's one exception. */
  poNumber: string | null
  /** Present on the detail read only when payment history is enabled. */
  payments?: ClientPortalInvoicePayment[]
}

export const clientPortalApi = {
  async getInviteByToken(token: string): Promise<ClientPortalInviteData> {
    try {
      return await apiClient<ClientPortalInviteData>(
        `/client-portal/invites/token/${token}`,
      )
    } catch (error) {
      logger.error('[ClientPortalApi] Error fetching invite by token:', error)
      throw error
    }
  },

  async getInvitesByEmail(
    email: string,
  ): Promise<{ data: ClientPortalInviteData[] }> {
    try {
      return await apiClient<{ data: ClientPortalInviteData[] }>(
        `/client-portal/invites/email/${encodeURIComponent(email)}`,
      )
    } catch (error) {
      logger.error('[ClientPortalApi] Error fetching invites by email:', error)
      throw error
    }
  },

  async acceptInvite(data: AcceptClientPortalInviteDTO) {
    try {
      return await apiClient(`/client-portal/invites/accept`, {
        method: 'POST',
        body: data,
      })
    } catch (error) {
      logger.error('[ClientPortalApi] Error accepting invite:', error)
      throw error
    }
  },

  async getPortalOverview(entityId: string): Promise<ClientPortalOverview> {
    return apiClient<ClientPortalOverview>(
      `/entities/${entityId}/client-portal`,
    )
  },

  async updateSettings(entityId: string, data: UpdateClientPortalSettingsDTO) {
    return apiClient(`/entities/${entityId}/client-portal/settings`, {
      method: 'PATCH',
      body: data,
    })
  },

  async updateShares(entityId: string, data: UpdateClientPortalSharesDTO) {
    return apiClient(`/entities/${entityId}/client-portal/shares`, {
      method: 'PUT',
      body: data,
    })
  },

  async addThreadShare(
    entityId: string,
    threadId: string,
  ): Promise<{ shared: boolean }> {
    return apiClient<{ shared: boolean }>(
      `/entities/${entityId}/client-portal/shares/thread/${threadId}`,
      { method: 'POST' },
    )
  },

  async listShareableResources(
    entityId: string,
  ): Promise<ClientPortalShareableResources> {
    return apiClient<ClientPortalShareableResources>(
      `/entities/${entityId}/client-portal/shareable`,
    )
  },

  async createInvite(entityId: string, data: CreateClientPortalInviteDTO) {
    const invite = await apiClient(
      `/entities/${entityId}/client-portal/invites`,
      {
        method: 'POST',
        body: data,
      },
    )
    trackFeatureUsed('client_portal_invite')
    return invite
  },

  async resendInvite(entityId: string, inviteId: string) {
    return apiClient(
      `/entities/${entityId}/client-portal/invites/${inviteId}/resend`,
      {
        method: 'POST',
      },
    )
  },

  async cancelInvite(entityId: string, inviteId: string) {
    return apiClient(
      `/entities/${entityId}/client-portal/invites/${inviteId}/cancel`,
      {
        method: 'POST',
      },
    )
  },

  async revokeAccess(entityId: string, accessId: string) {
    return apiClient(
      `/entities/${entityId}/client-portal/access/${accessId}/revoke`,
      {
        method: 'POST',
      },
    )
  },

  async reinstateAccess(
    entityId: string,
    accessId: string,
    grant?: { templateId?: string | null; permissions?: PortalPermissionMap },
  ) {
    return apiClient(
      `/entities/${entityId}/client-portal/access/${accessId}/reinstate`,
      {
        method: 'POST',
        body: grant ?? {},
      },
    )
  },

  // Per-user permission grants (entity-scoped)
  async updateAccessPermissions(
    entityId: string,
    accessId: string,
    permissions: PortalPermissionMap,
  ) {
    return apiClient(
      `/entities/${entityId}/client-portal/access/${accessId}/permissions`,
      {
        method: 'PATCH',
        body: { permissions },
      },
    )
  },

  // Org-level permission template CRUD
  async getPortalPermissionTemplates(): Promise<
    ClientPortalPermissionTemplate[]
  > {
    return apiClient<ClientPortalPermissionTemplate[]>(
      `/portal-permission-templates`,
    )
  },

  async createPortalPermissionTemplate(
    data: CreateClientPortalPermissionTemplateDTO,
  ): Promise<ClientPortalPermissionTemplate> {
    return apiClient<ClientPortalPermissionTemplate>(
      `/portal-permission-templates`,
      { method: 'POST', body: data },
    )
  },

  async updatePortalPermissionTemplate(
    id: string,
    data: UpdateClientPortalPermissionTemplateDTO,
  ): Promise<ClientPortalPermissionTemplate> {
    return apiClient<ClientPortalPermissionTemplate>(
      `/portal-permission-templates/${id}`,
      { method: 'PUT', body: data },
    )
  },

  async deletePortalPermissionTemplate(id: string) {
    return apiClient(`/portal-permission-templates/${id}`, {
      method: 'DELETE',
    })
  },

  async listProjects(entityId: string): Promise<ClientPortalProject[]> {
    return apiClient<ClientPortalProject[]>(
      `/client-portal/entities/${entityId}/projects`,
    )
  },

  // ---- Tickets -----------------------------------------------------------

  async listTickets(
    entityId: string,
    options: {
      status?: 'open' | 'closed' | 'all'
      page?: number
      limit?: number
      search?: string
      priority?: string
      typeDefId?: string
      sortBy?: 'updatedAt' | 'createdAt' | 'subject' | 'ticketNumber'
      sortOrder?: 'asc' | 'desc'
    } = {},
  ): Promise<{
    data: PortalTicketSummary[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    const params = new URLSearchParams()
    if (options.status) params.set('status', options.status)
    if (options.page) params.set('page', String(options.page))
    if (options.limit) params.set('limit', String(options.limit))
    if (options.search?.trim()) params.set('search', options.search.trim())
    if (options.priority) params.set('priority', options.priority)
    if (options.typeDefId) params.set('typeDefId', options.typeDefId)
    if (options.sortBy) params.set('sortBy', options.sortBy)
    if (options.sortOrder) params.set('sortOrder', options.sortOrder)
    const q = params.toString()
    return apiClient(
      `/client-portal/entities/${entityId}/tickets${q ? `?${q}` : ''}`,
    )
  },

  async getTicket(
    entityId: string,
    ticketId: string,
  ): Promise<{
    ticket: PortalTicketDetail
    communications: PortalTicketCommunication[]
  }> {
    return apiClient(`/client-portal/entities/${entityId}/tickets/${ticketId}`)
  },

  async getTicketAttachmentSignedUrl(
    entityId: string,
    ticketId: string,
    communicationId: string,
    attachmentId: string,
  ): Promise<{ url: string; fileName: string; mimeType: string }> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/${ticketId}/communications/${communicationId}/attachments/${attachmentId}/signed-url`,
    )
  },

  async createTicket(
    entityId: string,
    body: {
      subject: string
      description?: string
      priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
      typeDefId?: string | null
      draftId?: string
      attachments?: TicketStagedAttachment[]
    },
  ): Promise<{ id: string; ticketNumber: string }> {
    return apiClient(`/client-portal/entities/${entityId}/tickets`, {
      method: 'POST',
      body,
    })
  },

  // ----- Ticket drafts (portal) -----
  async listDrafts(
    entityId: string,
    options: { page?: number; limit?: number } = {},
  ): Promise<{
    data: TicketDraftListItem[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    const params = new URLSearchParams()
    if (options.page) params.set('page', String(options.page))
    if (options.limit) params.set('limit', String(options.limit))
    const q = params.toString()
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/drafts${q ? `?${q}` : ''}`,
    )
  },

  async getDraft(entityId: string, id: string): Promise<TicketDraft> {
    return apiClient(`/client-portal/entities/${entityId}/tickets/drafts/${id}`)
  },

  async upsertDraft(
    entityId: string,
    id: string,
    body: UpsertTicketDraft,
  ): Promise<TicketDraft> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/drafts/${id}`,
      { method: 'PUT', body },
    )
  },

  async deleteDraft(entityId: string, id: string): Promise<{ ok: true }> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/drafts/${id}`,
      { method: 'DELETE' },
    )
  },

  async submitDraft(
    entityId: string,
    id: string,
  ): Promise<{ id: string; ticketNumber: string }> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/drafts/${id}/submit`,
      { method: 'POST' },
    )
  },

  async replyTicket(
    entityId: string,
    ticketId: string,
    body: {
      bodyText: string
      bodyHtml?: string
      replyDraftId?: string
      attachments?: TicketStagedAttachment[]
    },
  ): Promise<{ id: string; createdAt: string }> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/${ticketId}/reply`,
      { method: 'POST', body },
    )
  },

  async closeTicket(
    entityId: string,
    ticketId: string,
  ): Promise<{ id: string; closedAt: string }> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/${ticketId}/close`,
      { method: 'POST' },
    )
  },

  /**
   * CSAT satisfaction survey state for a resolved ticket. Null when the
   * ticket isn't survey-eligible (type opted out or not resolved yet).
   */
  async getTicketCsat(
    entityId: string,
    ticketId: string,
  ): Promise<PortalTicketCsat | null> {
    return emptyResponseToNull(
      await apiClient<PortalTicketCsat | null>(
        `/client-portal/entities/${entityId}/tickets/${ticketId}/csat`,
      ),
    )
  },

  async submitTicketCsat(
    entityId: string,
    ticketId: string,
    body: { rating: number; comment?: string },
  ): Promise<PortalTicketCsat | null> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/${ticketId}/csat`,
      { method: 'POST', body },
    )
  },

  /**
   * The signed-in portal user's unsent reply composer for a ticket, synced
   * server-side so it can be resumed from any device. Saving an empty draft
   * deletes it; sending the reply deletes it server-side.
   */
  async getTicketReplyDraft(
    entityId: string,
    ticketId: string,
  ): Promise<PortalTicketReplyDraft | null> {
    return emptyResponseToNull(
      await apiClient<PortalTicketReplyDraft | null>(
        `/client-portal/entities/${entityId}/tickets/${ticketId}/reply-draft`,
      ),
    )
  },

  async saveTicketReplyDraft(
    entityId: string,
    ticketId: string,
    body: {
      bodyHtml: string
      ccText: string
      replyDraftId: string
      attachments: TicketStagedAttachment[]
    },
  ): Promise<{ ok: true }> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/${ticketId}/reply-draft`,
      { method: 'PUT', body },
    )
  },

  async deleteTicketReplyDraft(
    entityId: string,
    ticketId: string,
  ): Promise<{ ok: true }> {
    return apiClient(
      `/client-portal/entities/${entityId}/tickets/${ticketId}/reply-draft`,
      { method: 'DELETE' },
    )
  },

  async getSidebarCounts(
    entityId: string,
  ): Promise<{ unreadDocs: number; unreadUpdates: number }> {
    return apiClient<{ unreadDocs: number; unreadUpdates: number }>(
      `/client-portal/entities/${entityId}/sidebar-counts`,
    )
  },

  async getAttention(entityId: string): Promise<AttentionResponse> {
    return apiClient<AttentionResponse>(
      `/client-portal/entities/${entityId}/attention`,
    )
  },

  async getNotificationFeed(
    entityId: string,
    limit = 20,
    offset = 0,
  ): Promise<PortalNotificationFeedResponse> {
    return apiClient<PortalNotificationFeedResponse>(
      `/client-portal/entities/${entityId}/notifications?limit=${limit}&offset=${offset}`,
    )
  },

  async markAllNotificationsRead(
    entityId: string,
  ): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>(
      `/client-portal/entities/${entityId}/notifications/read-all`,
      { method: 'POST' },
    )
  },

  async listActionTasks(
    entityId: string,
    limit = 5,
  ): Promise<ClientActionTasksResponse> {
    return apiClient<ClientActionTasksResponse>(
      `/client-portal/entities/${entityId}/action-tasks?limit=${limit}`,
    )
  },

  async dismissNotification(notificationId: string): Promise<void> {
    await apiClient<void>(
      `/client-portal/notifications/${notificationId}/dismiss`,
      { method: 'POST' },
    )
  },

  async markNotificationRead(notificationId: string): Promise<void> {
    await apiClient<void>(
      `/client-portal/notifications/${notificationId}/read`,
      { method: 'POST' },
    )
  },

  async getNotificationPreferences(
    entityId: string,
  ): Promise<NotificationPreference[]> {
    return apiClient<NotificationPreference[]>(
      `/client-portal/entities/${entityId}/notification-preferences`,
    )
  },

  async updateNotificationPreferences(
    entityId: string,
    data: BulkUpdateNotificationPreferencesDto,
  ): Promise<{ updated: number; preferences: NotificationPreference[] }> {
    return apiClient<{
      updated: number
      preferences: NotificationPreference[]
    }>(`/client-portal/entities/${entityId}/notification-preferences`, {
      method: 'PUT',
      body: data,
    })
  },

  async updateNotificationPreference(
    entityId: string,
    data: UpdateNotificationPreferenceDto,
  ): Promise<NotificationPreference> {
    return apiClient<NotificationPreference>(
      `/client-portal/entities/${entityId}/notification-preferences/${data.channel}/${data.type}`,
      { method: 'PUT', body: data },
    )
  },

  async markNoteRead(entityId: string, noteId: string): Promise<void> {
    await apiClient<void>(
      `/client-portal/entities/${entityId}/notes/${noteId}/read`,
      { method: 'POST' },
    )
  },

  async markThreadRead(entityId: string, threadId: string): Promise<void> {
    await apiClient<void>(
      `/client-portal/entities/${entityId}/threads/${threadId}/read`,
      { method: 'POST' },
    )
  },

  async getActivity(
    entityId: string,
    limit: number = 20,
  ): Promise<ActivityResponse> {
    return apiClient<ActivityResponse>(
      `/client-portal/entities/${entityId}/activity?limit=${limit}`,
    )
  },

  async getMyEntities(): Promise<MyEntitiesResponse> {
    return apiClient<MyEntitiesResponse>(`/client-portal/my-entities`)
  },

  async listRetainers(entityId: string): Promise<ClientPortalRetainer[]> {
    return apiClient<ClientPortalRetainer[]>(
      `/client-portal/entities/${entityId}/retainers`,
    )
  },

  async getRetainer(
    entityId: string,
    contractId: string,
  ): Promise<ClientPortalRetainer> {
    return apiClient<ClientPortalRetainer>(
      `/client-portal/entities/${entityId}/retainers/${contractId}`,
    )
  },

  async listInvoices(entityId: string): Promise<ClientPortalInvoice[]> {
    return apiClient<ClientPortalInvoice[]>(
      `/client-portal/entities/${entityId}/invoices`,
    )
  },

  async getInvoice(
    entityId: string,
    invoiceId: string,
  ): Promise<ClientPortalInvoice> {
    return apiClient<ClientPortalInvoice>(
      `/client-portal/entities/${entityId}/invoices/${invoiceId}`,
    )
  },

  async downloadInvoicePdf(entityId: string, invoiceId: string): Promise<Blob> {
    return requestBlob(
      `/client-portal/entities/${entityId}/invoices/${invoiceId}/pdf`,
      { acceptType: 'application/pdf' },
    )
  },

  async listInvoiceApprovals(
    entityId: string,
  ): Promise<PortalInvoiceApproval[]> {
    return apiClient<PortalInvoiceApproval[]>(
      `/client-portal/entities/${entityId}/invoice-approvals`,
    )
  },

  async approveInvoice(
    entityId: string,
    requestId: string,
    comment?: string,
  ): Promise<InvoiceApprovalRequestWithTrail> {
    return apiClient<InvoiceApprovalRequestWithTrail>(
      `/client-portal/entities/${entityId}/invoice-approvals/${requestId}/approve`,
      { method: 'POST', body: { comment: comment || undefined } },
    )
  },

  async requestInvoiceChanges(
    entityId: string,
    requestId: string,
    comment: string,
  ): Promise<InvoiceApprovalRequestWithTrail> {
    return apiClient<InvoiceApprovalRequestWithTrail>(
      `/client-portal/entities/${entityId}/invoice-approvals/${requestId}/request-changes`,
      { method: 'POST', body: { comment } },
    )
  },

  async commentInvoiceApproval(
    entityId: string,
    requestId: string,
    comment: string,
  ): Promise<InvoiceApprovalRequestWithTrail> {
    return apiClient<InvoiceApprovalRequestWithTrail>(
      `/client-portal/entities/${entityId}/invoice-approvals/${requestId}/comments`,
      { method: 'POST', body: { comment } },
    )
  },

  // ---- Expense coding (Phase 3)

  async listExpenseCodingItems(
    entityId: string,
  ): Promise<PortalExpenseCodingListDTO> {
    return apiClient<PortalExpenseCodingListDTO>(
      `/client-portal/entities/${entityId}/expense-coding`,
    )
  },

  async getExpenseCodingConfig(
    entityId: string,
  ): Promise<ExpenseCodingConfigDTO> {
    return apiClient<ExpenseCodingConfigDTO>(
      `/client-portal/entities/${entityId}/expense-coding/config`,
    )
  },

  async createExpenseCodingItem(
    entityId: string,
    payload: PortalCreateCodingItemDTO,
  ): Promise<PortalExpenseCodingItemDTO> {
    return apiClient<PortalExpenseCodingItemDTO>(
      `/client-portal/entities/${entityId}/expense-coding`,
      { method: 'POST', body: payload },
    )
  },

  async previewExpenseCodingImport(
    entityId: string,
    file: File,
  ): Promise<ExpenseCodingImportPreviewDTO> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient<ExpenseCodingImportPreviewDTO>(
      `/client-portal/entities/${entityId}/expense-coding/import/preview`,
      { method: 'POST', body: formData },
    )
  },

  async commitExpenseCodingImport(
    entityId: string,
    rows: PortalCreateCodingItemDTO[],
  ): Promise<ExpenseCodingImportCommitResultDTO> {
    return apiClient<ExpenseCodingImportCommitResultDTO>(
      `/client-portal/entities/${entityId}/expense-coding/import/commit`,
      { method: 'POST', body: { rows } },
    )
  },

  async codeExpenseCodingItem(
    entityId: string,
    itemId: string,
    payload: PortalCodeItemDTO,
  ): Promise<PortalExpenseCodingItemDTO> {
    return apiClient<PortalExpenseCodingItemDTO>(
      `/client-portal/entities/${entityId}/expense-coding/${itemId}`,
      { method: 'PATCH', body: payload },
    )
  },

  async deleteExpenseCodingItem(
    entityId: string,
    itemId: string,
  ): Promise<void> {
    await apiClient(
      `/client-portal/entities/${entityId}/expense-coding/${itemId}`,
      { method: 'DELETE' },
    )
  },

  async listDeployments(entityId: string): Promise<PortalDeployment[]> {
    return apiClient<PortalDeployment[]>(
      `/client-portal/entities/${entityId}/deployments`,
    )
  },

  async getPortalSummary(entityId: string): Promise<ClientPortalSummary> {
    return apiClient<ClientPortalSummary>(`/client-portal/entities/${entityId}`)
  },

  async listNotes(entityId: string): Promise<ClientPortalNote[]> {
    return apiClient<ClientPortalNote[]>(
      `/client-portal/entities/${entityId}/notes`,
    )
  },

  async createNote(
    entityId: string,
    title?: string,
  ): Promise<ClientPortalNote> {
    return apiClient<ClientPortalNote>(
      `/client-portal/entities/${entityId}/notes`,
      { method: 'POST', body: { title: title ?? '' } },
    )
  },

  async deleteNote(entityId: string, noteId: string): Promise<void> {
    return apiClient<void>(
      `/client-portal/entities/${entityId}/notes/${noteId}`,
      { method: 'DELETE' },
    )
  },

  async getNote(entityId: string, noteId: string): Promise<ClientPortalNote> {
    return apiClient<ClientPortalNote>(
      `/client-portal/entities/${entityId}/notes/${noteId}`,
    )
  },

  async updateNote(
    entityId: string,
    noteId: string,
    data: UpdateNoteInput,
  ): Promise<ClientPortalNote> {
    return apiClient<ClientPortalNote>(
      `/client-portal/entities/${entityId}/notes/${noteId}`,
      {
        method: 'PUT',
        body: data,
      },
    )
  },

  async getNoteContent(
    entityId: string,
    noteId: string,
  ): Promise<{ noteId: string; content: string | null }> {
    return apiClient<{ noteId: string; content: string | null }>(
      `/client-portal/entities/${entityId}/notes/${noteId}/content`,
    )
  },

  async updateNoteContent(
    entityId: string,
    noteId: string,
    content: string,
    contentHtml?: string,
  ): Promise<ClientPortalNote> {
    return apiClient<ClientPortalNote>(
      `/client-portal/entities/${entityId}/notes/${noteId}/content`,
      {
        method: 'PUT',
        body: { content, contentHtml },
      },
    )
  },

  async listNotePages(
    entityId: string,
    noteId: string,
  ): Promise<NotePageSummaryWithCreator[]> {
    const response = await apiClient<{ pages: NotePageSummaryWithCreator[] }>(
      `/client-portal/entities/${entityId}/notes/${noteId}/pages`,
    )
    return response.pages
  },

  async getNotePage(
    entityId: string,
    noteId: string,
    pageId: string,
  ): Promise<NotePageWithCreator> {
    const response = await apiClient<{ page: NotePageWithCreator }>(
      `/client-portal/entities/${entityId}/notes/${noteId}/pages/${pageId}`,
    )
    return response.page
  },

  async createNotePage(
    entityId: string,
    noteId: string,
    data: CreateNotePage,
  ): Promise<NotePageWithCreator> {
    const response = await apiClient<{ page: NotePageWithCreator }>(
      `/client-portal/entities/${entityId}/notes/${noteId}/pages`,
      {
        method: 'POST',
        body: data,
      },
    )
    return response.page
  },

  async updateNotePage(
    entityId: string,
    noteId: string,
    pageId: string,
    data: UpdateNotePage,
  ): Promise<NotePageWithCreator> {
    const response = await apiClient<{ page: NotePageWithCreator }>(
      `/client-portal/entities/${entityId}/notes/${noteId}/pages/${pageId}`,
      {
        method: 'PUT',
        body: data,
      },
    )
    return response.page
  },

  async deleteNotePage(
    entityId: string,
    noteId: string,
    pageId: string,
  ): Promise<void> {
    await apiClient<void>(
      `/client-portal/entities/${entityId}/notes/${noteId}/pages/${pageId}`,
      {
        method: 'DELETE',
      },
    )
  },

  async reorderNotePages(
    entityId: string,
    noteId: string,
    pageIds: string[],
  ): Promise<void> {
    await apiClient<void>(
      `/client-portal/entities/${entityId}/notes/${noteId}/pages/reorder`,
      {
        method: 'PUT',
        body: { pageIds },
      },
    )
  },

  async getNotePageContent(
    entityId: string,
    noteId: string,
    pageId: string,
  ): Promise<{ pageId: string; content: string | null }> {
    return apiClient<{ pageId: string; content: string | null }>(
      `/client-portal/entities/${entityId}/notes/${noteId}/pages/${pageId}/content`,
    )
  },

  async updateNotePageContent(
    entityId: string,
    noteId: string,
    pageId: string,
    content: string,
    contentHtml?: string,
  ): Promise<{ success: boolean }> {
    return apiClient<{ success: boolean }>(
      `/client-portal/entities/${entityId}/notes/${noteId}/pages/${pageId}/content`,
      {
        method: 'PUT',
        body: { content, contentHtml },
      },
    )
  },

  async generateNoteActionItems(
    entityId: string,
    noteId: string,
    notePageId?: string,
  ): Promise<ClientPortalNoteActionItem[]> {
    return apiClient<ClientPortalNoteActionItem[]>(
      `/client-portal/entities/${entityId}/notes/${noteId}/action-items/generate`,
      {
        method: 'POST',
        body: { notePageId },
      },
    )
  },

  async suggestNoteActionItemOrganization(
    entityId: string,
    noteId: string,
    projectId: string,
    notePageId?: string,
  ): Promise<ClientPortalNoteActionItem[]> {
    return apiClient<ClientPortalNoteActionItem[]>(
      `/client-portal/entities/${entityId}/notes/${noteId}/action-items/suggest-organization`,
      {
        method: 'POST',
        body: { projectId, notePageId },
      },
    )
  },

  async getNoteActionItems(
    entityId: string,
    noteId: string,
    notePageId?: string,
  ): Promise<ClientPortalNoteActionItem[]> {
    const params = new URLSearchParams()
    if (notePageId) params.set('notePageId', notePageId)
    const query = params.toString() ? `?${params.toString()}` : ''

    return apiClient<ClientPortalNoteActionItem[]>(
      `/client-portal/entities/${entityId}/notes/${noteId}/action-items${query}`,
    )
  },

  async updateNoteActionItem(
    entityId: string,
    noteId: string,
    itemId: string,
    data: {
      text?: string
      description?: string | null
      assigneeId?: string | null
      assigneeIds?: string[]
      dueDate?: string | null
      order?: number
      groupId?: string | null
      taskTypeDefId?: string | null
      targetProjectId?: string | null
    },
  ): Promise<ClientPortalNoteActionItem> {
    return apiClient<ClientPortalNoteActionItem>(
      `/client-portal/entities/${entityId}/notes/${noteId}/action-items/${itemId}`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  },

  async deleteNoteActionItem(
    entityId: string,
    noteId: string,
    itemId: string,
  ): Promise<void> {
    await apiClient<void>(
      `/client-portal/entities/${entityId}/notes/${noteId}/action-items/${itemId}`,
      {
        method: 'DELETE',
      },
    )
  },

  async convertNoteActionItemsToTasks(
    entityId: string,
    noteId: string,
    actionItemIds: string[],
    projectId: string,
    options?: ClientPortalConvertActionItemsOptions,
  ): Promise<{ taskIds: string[] }> {
    return apiClient<{ taskIds: string[] }>(
      `/client-portal/entities/${entityId}/notes/${noteId}/action-items/convert-to-tasks`,
      {
        method: 'POST',
        body: { actionItemIds, projectId, ...options },
      },
    )
  },

  async listThreads(entityId: string): Promise<ClientPortalThread[]> {
    const threads = await apiClient<ClientPortalThread[]>(
      `/client-portal/entities/${entityId}/threads`,
    )
    logger.info('[ClientPortalApi] listThreads response', {
      entityId,
      count: Array.isArray(threads) ? threads.length : -1,
      threadIds: Array.isArray(threads) ? threads.map(thread => thread.id) : [],
    })
    return threads
  },

  async getThread(
    entityId: string,
    threadId: string,
    limit?: number,
    includeComments?: boolean,
  ): Promise<ClientPortalThreadDetailResponse> {
    const params = new URLSearchParams()
    if (limit) params.set('limit', String(limit))
    if (typeof includeComments === 'boolean') {
      params.set('includeComments', String(includeComments))
    }
    const query = params.toString() ? `?${params.toString()}` : ''
    const response = await apiClient<ClientPortalThreadDetailResponse>(
      `/client-portal/entities/${entityId}/threads/${threadId}${query}`,
    )
    logger.info('[ClientPortalApi] getThread response', {
      entityId,
      threadId,
      includeComments,
      limit,
      resolvedThreadId: response?.thread?.id ?? null,
      commentCount: Array.isArray(response?.comments)
        ? response.comments.length
        : null,
      totalComments: response?.totalComments ?? null,
    })
    return response
  },

  async getThreadComments(
    entityId: string,
    threadId: string,
    params?: {
      parentCommentId?: string | null
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
      limit?: number
      page?: number
    },
  ): Promise<PaginatedResponse<ThreadComment>> {
    const queryParams = new URLSearchParams()
    if (params?.parentCommentId === null) {
      queryParams.set('parentCommentId', 'null')
    } else if (params?.parentCommentId) {
      queryParams.set('parentCommentId', params.parentCommentId)
    }
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder)
    if (typeof params?.limit === 'number')
      queryParams.set('limit', String(params.limit))
    if (typeof params?.page === 'number')
      queryParams.set('page', String(params.page))

    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await apiClient<PaginatedResponse<ThreadComment>>(
      `/client-portal/entities/${entityId}/threads/${threadId}/comments${query}`,
    )
    logger.info('[ClientPortalApi] getThreadComments response', {
      entityId,
      threadId,
      parentCommentId: params?.parentCommentId ?? null,
      page: params?.page ?? 1,
      limit: params?.limit ?? null,
      count: Array.isArray(response?.data) ? response.data.length : null,
      totalItems: response?.meta?.totalItems ?? null,
      hasNextPage: response?.meta?.hasNextPage ?? null,
    })
    return response
  },

  async getThreadCommentChanges(
    entityId: string,
    threadId: string,
    params?: {
      sinceSequence?: number
      sinceUpdatedAt?: string
      limit?: number
    },
  ): Promise<ThreadCommentChangesResponse> {
    const queryParams = new URLSearchParams()
    if (typeof params?.sinceSequence === 'number') {
      queryParams.set('sinceSequence', String(params.sinceSequence))
    }
    if (params?.sinceUpdatedAt) {
      queryParams.set('sinceUpdatedAt', params.sinceUpdatedAt)
    }
    if (typeof params?.limit === 'number') {
      queryParams.set('limit', String(params.limit))
    }

    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiClient<ThreadCommentChangesResponse>(
      `/client-portal/entities/${entityId}/threads/${threadId}/comments/changes${query}`,
    )
  },

  async createThreadComment(
    entityId: string,
    threadId: string,
    data: CreateThreadComment,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/threads/${threadId}/comments`,
      {
        method: 'POST',
        body: data,
      },
    )
  },

  async updateThreadComment(
    entityId: string,
    threadId: string,
    commentId: string,
    content: string,
  ) {
    return apiClient<ThreadComment>(
      `/client-portal/entities/${entityId}/threads/${threadId}/comments/${commentId}`,
      {
        method: 'PUT',
        body: { content },
      },
    )
  },

  async toggleThreadReaction(
    entityId: string,
    threadId: string,
    reaction: string,
  ): Promise<{ reaction: ThreadReaction | null; added: boolean }> {
    return apiClient(
      `/client-portal/entities/${entityId}/threads/${threadId}/reactions/toggle`,
      {
        method: 'POST',
        body: { reaction },
      },
    )
  },

  async toggleCommentReaction(
    entityId: string,
    threadId: string,
    commentId: string,
    reaction: string,
  ): Promise<{ reaction: ThreadCommentReaction | null; added: boolean }> {
    return apiClient(
      `/client-portal/entities/${entityId}/threads/${threadId}/comments/${commentId}/reactions/toggle`,
      {
        method: 'POST',
        body: { reaction },
      },
    )
  },

  async deleteComment(entityId: string, threadId: string, commentId: string) {
    return apiClient(
      `/client-portal/entities/${entityId}/threads/${threadId}/comments/${commentId}`,
      {
        method: 'DELETE',
      },
    )
  },

  async uploadFile(
    entityId: string,
    moduleType: string,
    id: string,
    file: File,
    isAsset = false,
    // When true, the file is staged without a Document record — used for
    // ticket message attachments (tracked as CommunicationAttachment). The
    // response then carries { key, fileName, fileSize, mimeType }.
    skipDocument = false,
  ) {
    const formData = new FormData()
    formData.append('file', file)

    const params = new URLSearchParams({ asset: String(isAsset) })
    if (skipDocument) params.set('document', 'false')

    return apiClient(
      `/client-portal/entities/${entityId}/files/upload/${moduleType}/${id}?${params.toString()}`,
      {
        method: 'POST',
        body: formData,
      },
    )
  },

  async uploadTicketAttachment(
    entityId: string,
    draftId: string,
    file: File,
  ): Promise<TicketStagedAttachment> {
    const res = (await this.uploadFile(
      entityId,
      'ticket',
      draftId,
      file,
      false,
      true,
    )) as {
      key: string
      url?: string
      fileName?: string
      fileSize?: number
      mimeType?: string
    }
    return {
      storageKey: res.key,
      fileName: res.fileName ?? file.name,
      fileSize: res.fileSize ?? file.size,
      mimeType: res.mimeType ?? file.type,
      previewUrl: res.url,
    }
  },

  async getSignedUrls(
    entityId: string,
    keys: string[],
  ): Promise<Record<string, string>> {
    const response = await apiClient<{ signedUrls: Record<string, string> }>(
      `/client-portal/entities/${entityId}/files/signed-urls`,
      {
        method: 'POST',
        body: { keys },
      },
    )
    return response.signedUrls
  },

  async listDocumentsByModule(
    entityId: string,
    moduleType: string,
    moduleId: string,
  ): Promise<DocumentResponse[]> {
    return apiClient<DocumentResponse[]>(
      `/client-portal/entities/${entityId}/documents/by-module/${moduleType}/${moduleId}`,
    )
  },

  async listDocuments(
    entityId: string,
  ): Promise<PortalSharedDocumentsResponse> {
    return apiClient<PortalSharedDocumentsResponse>(
      `/client-portal/entities/${entityId}/documents`,
    )
  },

  async getDocumentSignedUrl(
    entityId: string,
    documentId: string,
  ): Promise<{ url: string; thumbnailUrl?: string }> {
    return apiClient<{ url: string; thumbnailUrl?: string }>(
      `/client-portal/entities/${entityId}/documents/${documentId}/signed-url`,
    )
  },

  async uploadPortalDocument(
    entityId: string,
    file: File,
  ): Promise<{ documentId?: string; key: string }> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient<{ documentId?: string; key: string }>(
      `/client-portal/entities/${entityId}/portal-documents`,
      { method: 'POST', body: formData },
    )
  },

  async createPortalLink(
    entityId: string,
    payload: { url: string; displayName?: string },
  ): Promise<DocumentResponse> {
    return apiClient<DocumentResponse>(
      `/client-portal/entities/${entityId}/portal-links`,
      { method: 'POST', body: payload },
    )
  },

  async deletePortalDocument(
    entityId: string,
    documentId: string,
  ): Promise<void> {
    await apiClient<void>(
      `/client-portal/entities/${entityId}/portal-documents/${documentId}`,
      { method: 'DELETE' },
    )
  },

  // =====================
  // Admin - Share Settings
  // =====================

  async listSharesWithSettings(
    entityId: string,
  ): Promise<ClientPortalShareWithSettings[]> {
    return apiClient<ClientPortalShareWithSettings[]>(
      `/entities/${entityId}/client-portal/shares`,
    )
  },

  async getProjectTaskSettings(
    entityId: string,
    shareId: string,
  ): Promise<ClientPortalProjectTaskSettings> {
    return apiClient<ClientPortalProjectTaskSettings>(
      `/entities/${entityId}/client-portal/shares/${shareId}/task-settings`,
    )
  },

  async updateProjectTaskSettings(
    entityId: string,
    shareId: string,
    data: UpdateClientPortalProjectTaskSettingsDTO,
  ) {
    return apiClient(
      `/entities/${entityId}/client-portal/shares/${shareId}/task-settings`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  },

  async getShareMemberSettings(
    entityId: string,
    shareId: string,
  ): Promise<ClientPortalShareMemberSetting[]> {
    return apiClient<ClientPortalShareMemberSetting[]>(
      `/entities/${entityId}/client-portal/shares/${shareId}/member-settings`,
    )
  },

  // All project-share capability overrides for a single member (keyed by
  // shareId). Projects the member hasn't customized are absent.
  async getMemberProjectSettings(
    entityId: string,
    accessId: string,
  ): Promise<ClientPortalMemberProjectSetting[]> {
    return apiClient<ClientPortalMemberProjectSetting[]>(
      `/entities/${entityId}/client-portal/members/${accessId}/project-settings`,
    )
  },

  async updateShareMemberTaskSettings(
    entityId: string,
    shareId: string,
    accessId: string,
    data: ProjectTaskCapabilityMap,
  ): Promise<ClientPortalShareMemberSetting> {
    return apiClient<ClientPortalShareMemberSetting>(
      `/entities/${entityId}/client-portal/shares/${shareId}/members/${accessId}/task-settings`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  },

  async resetShareMemberTaskSettings(
    entityId: string,
    shareId: string,
    accessId: string,
  ) {
    return apiClient(
      `/entities/${entityId}/client-portal/shares/${shareId}/members/${accessId}/task-settings`,
      {
        method: 'DELETE',
      },
    )
  },

  async publishShare(entityId: string, shareId: string) {
    return apiClient(
      `/entities/${entityId}/client-portal/shares/${shareId}/publish`,
      {
        method: 'POST',
      },
    )
  },

  async hideShare(entityId: string, shareId: string) {
    return apiClient(
      `/entities/${entityId}/client-portal/shares/${shareId}/hide`,
      {
        method: 'POST',
      },
    )
  },

  // =====================
  // Client Portal - Project Tasks
  // =====================

  async getProject(
    entityId: string,
    projectId: string,
  ): Promise<ClientPortalProjectDetail> {
    return apiClient<ClientPortalProjectDetail>(
      `/client-portal/entities/${entityId}/projects/${projectId}`,
    )
  },

  async listProjectTasks(
    entityId: string,
    projectId: string,
    pagination?: { limit?: number; offset?: number },
  ): Promise<{
    tasks: ClientPortalTask[]
    taskSettings: ClientPortalProjectTaskSettings
  }> {
    const params = new URLSearchParams()
    if (pagination?.limit) params.set('limit', String(pagination.limit))
    if (pagination?.offset) params.set('offset', String(pagination.offset))
    const query = params.toString() ? `?${params.toString()}` : ''

    return apiClient<{
      tasks: ClientPortalTask[]
      taskSettings: ClientPortalProjectTaskSettings
    }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks${query}`,
    )
  },

  async getTaskReferences(
    entityId: string,
    projectId: string,
    taskId: string,
  ): Promise<PortalTicketReference[]> {
    return apiClient<PortalTicketReference[]>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/references`,
    )
  },

  async listProjectSprints(
    entityId: string,
    projectId: string,
  ): Promise<ClientPortalSprint[]> {
    return apiClient<ClientPortalSprint[]>(
      `/client-portal/entities/${entityId}/projects/${projectId}/sprints`,
    )
  },

  async listProjectCycles(
    entityId: string,
    projectId: string,
  ): Promise<ClientPortalCycle[]> {
    return apiClient<ClientPortalCycle[]>(
      `/client-portal/entities/${entityId}/projects/${projectId}/cycles`,
    )
  },

  async listProjectBuckets(
    entityId: string,
    projectId: string,
  ): Promise<ClientPortalBucket[]> {
    return apiClient<ClientPortalBucket[]>(
      `/client-portal/entities/${entityId}/projects/${projectId}/buckets`,
    )
  },

  async getProjectTaskGroups(
    entityId: string,
    projectId: string,
  ): Promise<ClientPortalTaskGroup[]> {
    return apiClient<ClientPortalTaskGroup[]>(
      `/client-portal/entities/${entityId}/projects/${projectId}/task-groups`,
    )
  },

  async getProjectStatuses(
    entityId: string,
    projectId: string,
  ): Promise<ClientPortalStatus[]> {
    return apiClient<ClientPortalStatus[]>(
      `/client-portal/entities/${entityId}/projects/${projectId}/statuses`,
    )
  },

  async getProjectCustomFields(
    entityId: string,
    projectId: string,
  ): Promise<ClientPortalCustomField[]> {
    return apiClient<ClientPortalCustomField[]>(
      `/client-portal/entities/${entityId}/projects/${projectId}/custom-fields`,
    )
  },

  async getTaskComments(
    entityId: string,
    projectId: string,
    taskId: string,
    limit?: number,
  ): Promise<ClientPortalTaskCommentsResponse> {
    const params = new URLSearchParams()
    if (limit) params.set('limit', String(limit))
    const query = params.toString() ? `?${params.toString()}` : ''

    return apiClient<ClientPortalTaskCommentsResponse>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/comments${query}`,
    )
  },

  async markTaskRead(
    entityId: string,
    projectId: string,
    taskId: string,
  ): Promise<void> {
    await apiClient<void>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/read`,
      { method: 'POST' },
    )
  },

  async createTaskComment(
    entityId: string,
    projectId: string,
    taskId: string,
    data: ClientPortalTaskCommentRequest,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/comments`,
      {
        method: 'POST',
        body: data,
      },
    )
  },

  async editTaskComment(
    entityId: string,
    projectId: string,
    taskId: string,
    commentId: string,
    content: string,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/comments/${commentId}`,
      {
        method: 'PUT',
        body: { content },
      },
    )
  },

  async deleteTaskComment(
    entityId: string,
    projectId: string,
    taskId: string,
    commentId: string,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/comments/${commentId}`,
      {
        method: 'DELETE',
      },
    )
  },

  async updateTaskDescription(
    entityId: string,
    projectId: string,
    taskId: string,
    description: string,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/description`,
      {
        method: 'PATCH',
        body: { description },
      },
    )
  },

  async updateTaskPriority(
    entityId: string,
    projectId: string,
    taskId: string,
    priority: string | null,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/priority`,
      {
        method: 'PATCH',
        body: { priority },
      },
    )
  },

  async updateTaskDueDate(
    entityId: string,
    projectId: string,
    taskId: string,
    dueDate: string | null,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/due-date`,
      {
        method: 'PATCH',
        body: { dueDate },
      },
    )
  },

  async reorderTasks(
    entityId: string,
    projectId: string,
    taskOrders: Array<{ taskId: string; order: string }>,
  ) {
    return apiClient<{ success: boolean }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/reorder`,
      {
        method: 'POST',
        body: { taskOrders },
      },
    )
  },

  async reorderSprintTasks(
    entityId: string,
    projectId: string,
    sprintId: string,
    taskOrders: Array<{ taskId: string; order?: string; sprintOrder?: number }>,
  ) {
    return apiClient<{ success: boolean }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/sprints/${sprintId}/tasks/reorder`,
      {
        method: 'POST',
        body: { taskOrders },
      },
    )
  },

  async changeTasksSprint(
    entityId: string,
    projectId: string,
    taskIds: string[],
    sprintId: string | null,
  ) {
    return apiClient<{ success: boolean }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/change-sprint`,
      {
        method: 'POST',
        body: { taskIds, sprintId },
      },
    )
  },

  async reorderBucketTasks(
    entityId: string,
    projectId: string,
    bucketId: string,
    taskOrders: Array<{ taskId: string; order?: string; bucketOrder?: number }>,
  ) {
    return apiClient<{ success: boolean }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/buckets/${bucketId}/tasks/reorder`,
      {
        method: 'POST',
        body: { taskOrders },
      },
    )
  },

  async changeTasksBucket(
    entityId: string,
    projectId: string,
    taskIds: string[],
    bucketId: string | null,
  ) {
    return apiClient<{ success: boolean }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/change-bucket`,
      {
        method: 'POST',
        body: { taskIds, bucketId },
      },
    )
  },

  async changeTasksGroup(
    entityId: string,
    projectId: string,
    taskIds: string[],
    groupId: string | null,
  ) {
    return apiClient<{ success: boolean }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/change-group`,
      {
        method: 'POST',
        body: { taskIds, groupId },
      },
    )
  },

  async changeTasksCycle(
    entityId: string,
    projectId: string,
    taskIds: string[],
    cycleInstanceId: string | null,
  ) {
    return apiClient<{ success: boolean }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/change-cycle`,
      {
        method: 'POST',
        body: { taskIds, cycleInstanceId },
      },
    )
  },

  async updateTaskCustomField(
    entityId: string,
    projectId: string,
    taskId: string,
    fieldId: string,
    value: unknown,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/custom-fields/${fieldId}`,
      {
        method: 'PATCH',
        body: { value },
      },
    )
  },

  async getTaskSubtasks(
    entityId: string,
    projectId: string,
    taskId: string,
  ): Promise<ClientPortalSubtasksResponse> {
    return apiClient<ClientPortalSubtasksResponse>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/subtasks`,
    )
  },

  async listTaskDocuments(
    entityId: string,
    projectId: string,
    taskId: string,
  ): Promise<DocumentResponse[]> {
    return apiClient<DocumentResponse[]>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/documents`,
    )
  },

  async getTaskDocumentSignedUrl(
    entityId: string,
    projectId: string,
    taskId: string,
    documentId: string,
  ): Promise<{ url: string; thumbnailUrl?: string }> {
    return apiClient<{ url: string; thumbnailUrl?: string }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/documents/${documentId}/signed-url`,
    )
  },

  async updateTaskStatus(
    entityId: string,
    projectId: string,
    taskId: string,
    data: UpdateClientPortalTaskStatusDTO,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/status`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  },

  async createTask(
    entityId: string,
    projectId: string,
    data: CreateClientPortalTaskDTO,
  ) {
    return apiClient(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks`,
      {
        method: 'POST',
        body: data,
      },
    )
  },

  async uploadTaskAttachment(
    entityId: string,
    projectId: string,
    taskId: string,
    file: File,
  ): Promise<{ documentId?: string; key: string }> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient<{ documentId?: string; key: string }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/documents`,
      { method: 'POST', body: formData },
    )
  },

  async deleteTaskAttachment(
    entityId: string,
    projectId: string,
    taskId: string,
    documentId: string,
  ): Promise<void> {
    await apiClient<void>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/documents/${documentId}`,
      { method: 'DELETE' },
    )
  },
}
